import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { FC, Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceProfile, JobHistoryItem } from '@/types/pages/storeManagement/device';
import JobDetailRow from './JobDetailRow';
import BaseCopyableText from '../ui/BaseCopyableText';
import { formatInitiator } from '@/utils/deviceDetailUtlis';
import TableCellBulletList from './TableCellBulletList';

type ProductSnapshotItem = {
    p_name?: string;
    p_barcode?: string;
};

type JobHistoryTableProps = {
    jobs: JobHistoryItem[];
    type: 'ap' | 'rt' | 'tag';
    profile?: DeviceProfile | null;
    formatUtcToStoreTime: (utcTime?: string | null) => string;
};

const parseProductSnapshot = (snapshot: string | null): ProductSnapshotItem[] => {
    if (!snapshot) return [];

    try {
        const parsed: unknown = JSON.parse(snapshot);

        if (!Array.isArray(parsed)) return [];

        return parsed.filter(
            (product): product is ProductSnapshotItem => typeof product === 'object' && product !== null,
        );
    } catch {
        return [];
    }
};

const getJobKey = (job: JobHistoryItem, index: number) => {
    return String(job.id ?? `${job.created_at}-${job.description ?? 'unknown'}-${index}`);
};

const ACTION_LABELS: Record<string, string> = {
    refresh_current_screen: 'Refresh Current Screen',
    refresh_main_screen: 'Refresh Main Screen',
    remote_command: 'Send Remote Command',
    deassociate: 'Deassociate Device',
    associate: 'Associate Device',
    wakeup_start: 'Start Wake-Up',
    wakeup_stop: 'Stop Wake-Up',
    dev_confirm: 'Confirm Device',
    delete_device: 'Delete Device',
    lock_device: 'Lock Device',
    unlock_device: 'Unlock Device',
    swch_start: 'Enable Switch Channel',
    swch_stop: 'Disable Switch Channel',
    image_push: 'Image Push',
};

const JobHistoryTable: FC<JobHistoryTableProps> = ({ jobs, type, profile, formatUtcToStoreTime }) => {
    const { t } = useTranslation('common');
    const [expandedJobKeys, setExpandedJobKeys] = useState<Set<string>>(new Set());
    const displayedJobs = jobs.slice(0, 10);
    const columns = useMemo(
        () => [
            {
                id: 'time',
                label: 'Time',
                width: 100,
                renderCell: (job: JobHistoryItem) => <BaseCopyableText value={formatUtcToStoreTime(job.created_at)} />,
            },
            {
                id: 'jobType',
                label: 'Job Type',
                width: 130,
                renderCell: (job: JobHistoryItem) => (
                    <BaseCopyableText value={ACTION_LABELS[job.description] ?? job.description} />
                ),
            },
            {
                id: 'productName',
                label: 'Product Name',
                width: 200,
                renderCell: (job: JobHistoryItem) => {
                    const products = parseProductSnapshot(job.product_snapshot);
                    return <TableCellBulletList list={products.map((product) => product.p_name || '')} />;
                },
            },
            {
                id: 'productBarcode',
                label: 'Product Barcode',
                width: 150,
                renderCell: (job: JobHistoryItem) => {
                    const products = parseProductSnapshot(job.product_snapshot);
                    return <TableCellBulletList list={products.map((product) => product.p_barcode || '')} />;
                },
            },
            {
                id: 'status',
                label: 'Status',
                width: 100,
                renderCell: (job: JobHistoryItem) =>
                    `${t(job.status) || '-'}${job.status_code ? ` (${job.status_code})` : ''}`,
            },
            {
                id: 'initiator',
                label: 'Initiator',
                width: 100,
                renderCell: (job: JobHistoryItem) => <BaseCopyableText value={formatInitiator(job.initiator)} />,
            },
        ],
        [formatUtcToStoreTime, t],
    );

    const handleToggle = (jobKey: string) => {
        setExpandedJobKeys((current) => {
            const next = new Set(current);

            if (next.has(jobKey)) {
                next.delete(jobKey);
            } else {
                next.add(jobKey);
            }

            return next;
        });
    };

    return (
        <TableContainer
            component={Paper}
            sx={{
                maxHeight: '60vh',
                overflow: 'auto',
                textAlign: 'left',
            }}
        >
            <Table
                stickyHeader
                size="small"
                sx={{
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                    '& th, & td': {
                        border: '1px solid',
                        borderColor: 'divider',
                        px: 1,
                        py: 0.5,
                    },
                    '& > thead th': {
                        bgcolor: 'background.paper',
                    },
                    '& .job-details-cell': {
                        p: 0,
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        {columns.map((column) => {
                            if (column.id === 'initiator' && type === 'tag') return null;
                            return (
                                <TableCell key={column.id} sx={{ width: column.width, whiteSpace: 'nowrap' }}>
                                    {t(column.label)}
                                </TableCell>
                            );
                        })}
                        {type === 'tag' && (
                            <TableCell align="center" sx={{ width: 70, whiteSpace: 'nowrap' }}>
                                {t('Details')}
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {displayedJobs.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center">
                                {t('No History')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        displayedJobs.map((job, index) => {
                            const jobKey = getJobKey(job, index);
                            const expanded = expandedJobKeys.has(jobKey);

                            return (
                                <Fragment key={jobKey}>
                                    <TableRow
                                        hover
                                        sx={{
                                            '& > td': {
                                                borderBottom: expanded ? 0 : undefined,
                                            },
                                        }}
                                    >
                                        {columns.map((column) => {
                                            if (column.id === 'initiator' && type === 'tag') return null;
                                            return (
                                                <TableCell sx={{ width: column.width }} key={column.id}>
                                                    {column.renderCell(job)}
                                                </TableCell>
                                            );
                                        })}
                                        {type === 'tag' && (
                                            <TableCell align="center" sx={{ width: 70 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggle(jobKey)}
                                                    aria-expanded={expanded}
                                                    aria-controls={`job-details-${jobKey}`}
                                                    aria-label={expanded ? t('Collapse details') : t('Expand details')}
                                                >
                                                    {expanded ? (
                                                        <KeyboardArrowUp fontSize="small" />
                                                    ) : (
                                                        <KeyboardArrowDown fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    {type === 'tag' && (
                                        <TableRow>
                                            <TableCell
                                                className="job-details-cell"
                                                colSpan={6}
                                                sx={{
                                                    borderTop: 0,
                                                    borderBottom: expanded ? undefined : 0,
                                                }}
                                            >
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    <Box id={`job-details-${jobKey}`}>
                                                        <JobDetailRow
                                                            job={job}
                                                            profile={profile}
                                                            formatUtcToStoreTime={formatUtcToStoreTime}
                                                        />
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default JobHistoryTable;
